import { AUFTRAG_API } from '@Constants/backendConstants';
import { type ApiRequisition } from '@Model';
import { useServerObject } from '@synqo/frontend-component-library';
import { useCallback, useEffect, useState } from 'react';

export interface PrefilledRequisition {
    /** The prefilled draft. `undefined` until the prefill request resolves successfully. */
    prefilled: ApiRequisition | undefined;
    /** Discards the current draft state and fetches a fresh prefilled one (e.g. "Weitere Bestellung"). */
    reload: () => void;
}

/**
 * Fetches a fresh prefilled `ApiRequisition` draft from the backend on mount.
 *
 * An empty primary key makes {@link useServerObject} post to `:prefill`, which mints a draft, while a
 * set one loads that draft. So the minted id is pinned as soon as it arrives, and
 * {@link PrefilledRequisition.reload} clears it again: the changed query key fetches the next draft.
 * A given draft is pinned from the start.
 *
 * @param givenDraftId The draft to load rather than minting one.
 */
// @ai-generated 2026-05-28T13:00:50+00:00 by matthias.gessinger@synqony.com using Claude Haiku
export function usePrefilledRequisition(givenDraftId?: string): PrefilledRequisition {
    const [pinnedDraftId, setPinnedDraftId] = useState(givenDraftId ?? '');
    const [discardedDraftId, setDiscardedDraftId] = useState('');

    const { result } = useServerObject<ApiRequisition>({
        apiResourcePath: `${AUFTRAG_API}/requisitions`,
        primaryKey: pinnedDraftId,
        include: ['all'],
    });

    const arrived = result.data;

    /**
     * The draft handed out, which is the last one that arrived.
     *
     * Pinning the id changes the query key, so a moment after the draft is minted there is no
     * answer again until the pinned load returns. The wizard is mounted on this, so letting that
     * gap show would tear the whole wizard down and build it back up one request later — every
     * step remounting, and every read a step does on mount running a second time. The draft
     * already in hand is the one that load answers with, so it stays until something newer
     * arrives.
     */
    // @ai-generated 2026-09-11T16:40:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
    const [draft, setDraft] = useState<ApiRequisition | undefined>(undefined);

    useEffect(() => {
        if (arrived) {
            setDraft(arrived);
        }
    }, [arrived]);

    const draftId = arrived?.id ?? '';

    useEffect(() => {
        if (pinnedDraftId === '' && draftId !== '' && draftId !== discardedDraftId) {
            setPinnedDraftId(draftId);
        }
    }, [pinnedDraftId, draftId, discardedDraftId]);

    // Remembering the draft we let go of matters: pinning it again would keep the wizard on an
    // order the server has already turned into a requisition, and every further save would fail.
    const reload = useCallback(() => {
        setDiscardedDraftId(draftId);
        setPinnedDraftId('');
        setDraft(undefined);
    }, [draftId]);

    return { prefilled: draft, reload };
}
